import { isNumber, sortBy } from 'lodash';

export const trimTrailingZeros = numberString => {
  if (isNumber(numberString)) {
    return numberString;
  }

  return numberString.replace(/^([+-]?\d+\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
};

export const HTMLtoText = html => {
  if (html === null || html === void 0) {
    return '';
  }

  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

/**
 * Determines if the provided argument is undefined or a valid string containing
 * only whitespace.
 * @param {String} str - the string to be tested
 * @returns {boolean} whether the provided argument is an empty string
 */
export const isEmptyString = str => {
 return !str || (typeof str === 'string' && str.trim() === '');
}

/**
 * Determines if the provided argument is a valid string with at least one
 * non-whitespace character.
 * @param {String} str - the string to be tested
 * @returns {boolean} whether the provided argument is a non-empty string
 */
export const isNonEmptyString = str => {
  return str !== undefined && str !== null && typeof str === 'string' && str.trim() !== '';// eslint-disable-line no-undefined
}

export const isValidEmail = email =>
    RegExp("^[A-Za-z0-9._+-]+@[A-Za-z0-9._+-]+\\.[A-Za-z0-9._+-]+$").test(email);

export const isValidOrBlankEmail = email =>
    isEmptyString(email) || isValidEmail(email);

export const ifFieldEmptyReturnDefault = (field, defaultVal) => {
  if (isEmptyString(field)) return defaultVal;
  return field;
}

export const isPositiveInteger = (number) => {
  return (/^[0-9]+$/).test(String(number));
}

/**
 * Checks if the given url is a valid url. Please note: this is not checking if the url itself is a "real website",
 * but is checking if the url has one of the expected formats of a website address. The regex is based on
 * https://stackoverflow.com/a/49849482/4487722
 * @param {string} url - The possible url
 * @returns {boolean} If the given url matches the url regex
 */
export const isValidUrl = url => 
    typeof url === 'string' && 
    (/(http(s)?:\/\/.)(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g).test(url);

/**
 * This is a helpful validation check if the url is optional (aka validation shouldn't fail on empty)
 * @param {string} url - The possible url
 * @returns {boolean} If the given url is empty or a valid url
 */
export const isValidOrBlankUrl = url => isEmptyString(url) || isValidUrl(url);

/**
 * Checks if the given phoneNumber is a valid phone number. Please note: this is not checking if the
 * number itself is a "real phone number", but is checking if the number has one of the expected formats
 * of a phone number. The regex is based on 
 * https://stackoverflow.com/questions/16699007/regular-expression-to-match-standard-10-digit-phone-number
 * @param {string} phoneNumber - The possible phone number
 * @returns {boolean} If the given phoneNumber matches the phone number regex
 */
export const isValidPhoneNumber = phoneNumber =>
    typeof phoneNumber === 'string' &&
    (/^((\+)?\d{1,2}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/).test(phoneNumber);

/**
 * This is a helpful validation check if the phone number is optional (aka validation shouldn't fail on empty)
 * @param {string} phoneNumber - The possible phone number
 * @returns {boolean} If the given phoneNumber is empty or a valid phone number
 */
export const isValidOrBlankPhoneNumber = phoneNumber => isEmptyString(phoneNumber) || isValidPhoneNumber(phoneNumber);

/**
 * Extends lodash sort to ignore case sensitivity if the object is a string.
 * @param {array} list of objects to be sorted
 * @param {string} key name of object property to sort by
 * @returns {*} sorted list
 */
export const caseInsensitiveSort = (list, key) =>
    sortBy(list, (item) => typeof item[key] === 'string' ? item[key].toLowerCase() : item[key]);
